import * as React from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  EditableText,
  H3,
  H5,
  Icon,
  Intent,
  RadioGroup,
  FormGroup,
  Radio,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import * as turf from "@turf/helpers";
import { ScrollView, Spacer, ArchiveSurveyDialog, useSurveyArchive } from "components/common/";
import { useCollapsibleTree } from "hooks/";
import {
  useSinkState,
  useSinkActions,
  useDrawingActions,
  useMapState,
} from "context/";
import LayerTree from "./LayerTree";
import ApiLayers from "../../../api/layers";
import ApiSurveys from "../../../api/surveys";
import UndoArchiveManager from "../../../services/UndoArchiveManager";
import { formatWGS84 } from "../../../utils/";
import { buildSurveyDetailFields } from "../../../utils/surveyDetails";
import * as projections from "../../../projections";
import { utcToZonedTime, format } from "date-fns-tz";
import { CheckEditAccess, useCheckViewAccess, useCheckAdminAccess } from "context/";
import useRefreshOnFocus from "hooks/useRefreshOnFocus";
import { useCanDeleteTags } from "hooks/useCanDeleteTags";
import { useDownloads } from "../../../hooks/useDownloads";
import turfCenter from "@turf/center";
import { TagEditorSlot } from "components/common/TagEditorSlot";
import { Outlet, useParams } from "react-router-dom";
import { SurveyWithExtras, isSurveyArchived } from "../../../interfaces";

const HeaderDetailRow = ({ label, value }) => {
  return (
    <div>
      <span
        css={css`
          display: inline-block;
          color: var(--neutral-4);
          margin-right: 0.5rem;
          width: 6rem;
        `}
      >
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
};

function EditableHeader({ survey, label, setLabel, onConfirm }) {
  const searchRef = React.useRef();

  const [input, setInput] = React.useState(null);

  React.useEffect(() => {
    if (searchRef.current) {
      if (searchRef.current.children[0].tagName === "DIV") {
        setInput(searchRef.current.children[0]);
      }
    }
  }, []);

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
      `}
      ref={searchRef}
    >
      <EditableText
        css={css`
          max-width: 320px;
          margin-right: 1rem;
        `}
        maxLength={64}
        placeholder="Edit label..."
        selectAllOnFocus
        value={label}
        onChange={setLabel}
        onConfirm={onConfirm}
        onCancel={() => setLabel(survey.label)}
      />
      <Icon icon="edit" onClick={() => input.focus && input.focus()} />
    </div>
  );
}

const SetNewSurveyLocation = (props) => {
  const [type, setType] = React.useState("map");

  const { resink } = useSinkActions();
  const { map } = useMapState();

  const getCoordsForSurvey = () => {
    const ortho = props.layers.find(
      ({ source_id }) => source_id === "orthomosaic"
    );

    if (ortho) {
      const source = Object.values(map.style.sourceCaches).find(({ id }) => {
        const source = map.getSource(id);

        return source.url === `mapbox://${ortho.source}`;
      });

      if (source) {
        const { bounds } = source.getSource();

        const rect = turf.points([
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ]);

        const center = turfCenter(rect);

        return {
          lat: center.geometry.coordinates[1],
          lng: center.geometry.coordinates[0],
        };
      }
    }

    const features = props.layers.reduce((features, layer) => {
      if (layer.annotations) {
        return features.concat(
          layer.annotations.map(({ geometry }) => turf.feature(geometry))
        );
      }

      return features;
    }, []);

    if (features.length === 0) {
      return null;
    }

    const collection = turf.featureCollection(features);

    const center = turfCenter(collection);

    return {
      lat: center.geometry.coordinates[1],
      lng: center.geometry.coordinates[0],
    };
  };

  const setSurveyLocationToMapCenter = () => {
    if (!map) return;

    const { lng, lat } = map.getCenter();
    const zoom = map.getZoom();

    const params = {
      location: JSON.stringify(turf.point([lng, lat]).geometry),
      zoom_start: parseFloat(zoom.toFixed(1)),
      log: "getSurveyLocationToMapCenter",
    };

    ApiSurveys.updateSurvey(props.survey.id, params).then(resink);
  };

  const setSurveyLocationToDataCenter = () => {
    const coords = getCoordsForSurvey();

    if (coords) {
      const { lat, lng } = coords;

      map.setCenter({ lat, lng });

      const params = {
        location: JSON.stringify(turf.point([lng, lat]).geometry),
        log: "setSurveyLocationToDataCenter",
      };

      ApiSurveys.updateSurvey(props.survey.id, params).then(resink);
    }
  };

  return (
    <Alert
      className="bp6-dark"
      cancelButtonText="Cancel"
      confirmButtonText="Confirm"
      icon="locate"
      intent={Intent.PRIMARY}
      isOpen={props.showLocationPopup}
      loading={false}
      onCancel={() => props.setShowLocationPopup(false)}
      onConfirm={() => {
        if (!map) return;

        if (type === "data") {
          setSurveyLocationToDataCenter();
        }

        if (type === "map") {
          setSurveyLocationToMapCenter();
        }

        props.setShowLocationPopup(false);
      }}
    >
      <FormGroup
        label="Set New Survey Location"
        helperText="Confirm to update the default location of this survey."
      >
        <RadioGroup
          name="group"
          onChange={(event) => setType(event.target.value)}
          selectedValue={type}
        >
          <Radio label="Center on current map view" value="map" />
          <Radio
            label="Center on the survey data"
            disabled={props.allowCenterOnSurveyData === false}
            value="data"
          />
        </RadioGroup>
      </FormGroup>
    </Alert>
  );
};

const SurveyDetails = ({ survey }) => {
  const { resink, getLayers } = useSinkActions();

  const {
    undoArchiveQueue,
    sink: { sites },
    ...state
  } = useSinkState();

  const hasTagEditorViewAccess = useCheckViewAccess(survey);
  const hasAdminAccess = useCheckAdminAccess(survey);
  const canDeleteTags = useCanDeleteTags();

  const isArchived = isSurveyArchived(survey);

  const { refetch: refetchDownloads } = useDownloads(survey.id);

  const [label, setLabel] = React.useState(survey.label || "");

  const location = formatWGS84(survey.coordinates);
  const layers = state.layers.filter((layer) => !layer.archived);

  const layersForSurvey = projections.layersForSurvey(survey.id, layers);
  const collapsibleTree = useCollapsibleTree();
  const { setActiveDrawingLayerImmediate } = useDrawingActions();

  const [showLocationPopup, setShowLocationPopup] = React.useState(false);
  const archive = useSurveyArchive(survey);

  useRefreshOnFocus(
    React.useCallback(() => {
      return Promise.all([getLayers(survey.id), refetchDownloads()]);
    }, [survey.id, getLayers, refetchDownloads])
  );

  return (
    <>
      {!isArchived && (
        <SetNewSurveyLocation
          survey={survey}
          layers={layersForSurvey}
          allowCenterOnSurveyData={layersForSurvey.length > 0}
          showLocationPopup={showLocationPopup}
          setShowLocationPopup={setShowLocationPopup}
        />
      )}
      <div
        css={css`
          padding: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          background-color: var(--midnight);
        `}
      >
        <Card>
          <div
            css={css`
              opacity: ${isArchived ? 0.65 : 1};
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
                margin-bottom: 10px;
              `}
            >
              <H3
                css={css`
                  margin-bottom: 0;
                `}
              >
                {isArchived ? (
                  label
                ) : (
                  <CheckEditAccess fallback={label}>
                    <EditableHeader
                      survey={survey}
                      label={label}
                      setLabel={setLabel}
                      onConfirm={(value) =>
                        ApiSurveys.updateSurvey(survey.id, {
                          label: value,
                        }).then(resink)
                      }
                    />
                  </CheckEditAccess>
                )}
              </H3>
              {!isArchived && hasAdminAccess && (
                <Button
                  minimal
                  icon="archive"
                  aria-label="Archive survey"
                  onClick={archive.open}
                  css={css`
                    color: var(--neutral-4);
                    flex-shrink: 0;
                    overflow: hidden;
                    max-width: 2rem;
                    padding-left: 6px;
                    padding-right: 6px;
                    transition:
                      max-width 160ms ease,
                      color 160ms ease,
                      padding 160ms ease;

                    .bp6-icon,
                    svg {
                      color: inherit !important;
                      fill: currentColor !important;
                    }

                    .bp6-button-text {
                      max-width: 0;
                      margin-left: 0;
                      opacity: 0;
                      white-space: nowrap;
                      overflow: hidden;
                      transition:
                        max-width 160ms ease,
                        margin-left 160ms ease,
                        opacity 120ms ease;
                    }

                    &:hover {
                      color: var(--danger) !important;
                      max-width: 7rem;
                      padding-right: 10px;
                    }

                    &:hover .bp6-icon,
                    &:hover svg,
                    &:hover .bp6-button-text {
                      color: var(--danger) !important;
                    }

                    &:hover .bp6-button-text {
                      max-width: 4rem;
                      margin-left: 0.25rem;
                      opacity: 1;
                    }
                  `}
                >
                  Archive
                </Button>
              )}
            </div>
            {hasAdminAccess && (
              <ArchiveSurveyDialog entityName={label} {...archive} />
            )}

            {survey.site_id && (
              <HeaderDetailRow
                label="Site"
                value={sites.find((site) => survey.site_id === site.id)?.label || "Unknown site"}
              />
            )}
            {survey.nominal_flight_date && (
              <HeaderDetailRow
                label="Date"
                value={format(
                  utcToZonedTime(survey.nominal_flight_date, survey.timezone),
                  "yyyy-MM-dd"
                )}
              />
            )}
            {survey.timezone && (
              <HeaderDetailRow label="Timezone" value={survey.timezone} />
            )}
            {location && (
              <HeaderDetailRow
                label="Coordinates"
                value={`${location.latitude}, ${location.longitude}`}
              />
            )}
            {survey.start_time && (
              <HeaderDetailRow
                label="Capture Start"
                value={format(
                  utcToZonedTime(new Date(survey.start_time), survey.timezone),
                  "HH:mm:ss zzz",
                  { timeZone: survey.timezone }
                )}
              />
            )}
            {survey.end_time && (
              <HeaderDetailRow
                label="Capture End"
                value={format(
                  utcToZonedTime(new Date(survey.end_time), survey.timezone),
                  "HH:mm:ss zzz",
                  { timeZone: survey.timezone }
                )}
              />
            )}
            {buildSurveyDetailFields(survey).map((field) => (
              <HeaderDetailRow
                key={field.key}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
          <Spacer />
          {isArchived ? (
            <>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  color: var(--neutral-3);
                  margin-bottom: 0.75rem;
                `}
              >
                <Icon icon="archive" />
                <span>This survey is archived.</span>
              </div>
              {hasAdminAccess && (
                <Button
                  fill
                  icon="unarchive"
                  intent="primary"
                  loading={archive.isRestoring}
                  disabled={archive.isRestoring}
                  onClick={archive.restore}
                >
                  Restore Survey
                </Button>
              )}
            </>
          ) : (
            <CheckEditAccess fallback={null}>
              <Button
                fill
                icon="locate"
                onClick={() => setShowLocationPopup(true)}
              >
                Set New Survey Location
              </Button>
            </CheckEditAccess>
          )}
        </Card>
        <div
          css={css`
            padding: 1rem 0 0 0;
          `}
        >
          {hasTagEditorViewAccess ? (
            <TagEditorSlot
              survey={survey as SurveyWithExtras}
              canDeleteTags={canDeleteTags}
              resink={resink}
              fontSize="0.875rem"
              marginRight="0.75rem"
            />
          ) : null}
        </div>
        <UndoArchiveWidget undoArchiveQueue={undoArchiveQueue} />
        <div
          css={css`
            margin-top: 1rem;
          `}
        >
          {!isArchived && (
            <CheckEditAccess fallback={null}>
              <Button
                icon="new-layer"
                onClick={() =>
                  ApiLayers.createAnnotationLayer(survey.id).then((layer) =>
                    setActiveDrawingLayerImmediate(layer)
                  )
                }
              >
                New Markup Layer
              </Button>
            </CheckEditAccess>
          )}
        </div>
      </div>
      <ScrollView>
        <div
          css={css`
            padding-top: 0.5rem;
            padding-bottom: 1rem;
          `}
        >
          <LayerTree layers={layersForSurvey} {...collapsibleTree} />
        </div>
      </ScrollView>
      <Outlet />
    </>
  );
};

const SurveyDetailsNavigator = () => {
  const params = useParams();
  const { surveys } = useSinkState();

  const survey = surveys.find((s) => s.id === Number(params.id));

  if (!survey) {
    return null;
  }

  return <SurveyDetails key={survey.id} survey={survey} />;
};

const formatReport = (value, label) => {
  if (value === 0) return "";
  return `${value} ${label}${value > 1 ? "s" : ""}.`;
};

const UndoArchiveWidget = ({ undoArchiveQueue }) => {
  const layerCount = undoArchiveQueue.filter(
    (entry) => entry.type === "layer"
  ).length;
  const layerReport = formatReport(layerCount, "layer");

  const annotationCount = undoArchiveQueue.filter(
    (entry) => entry.type === "annotation"
  ).length;
  const annotationReport = formatReport(annotationCount, "annotation");

  if (undoArchiveQueue.length === 0) return null;

  return (
    <Card
      css={css`
        margin-top: 1rem;
      `}
    >
      <H5>
        Deleted {layerReport}
        {(layerCount && annotationCount && " and ") || ""} {annotationReport}
      </H5>
      <ButtonGroup fill>
        <Button
          icon="undo"
          text="Undo Last"
          onClick={UndoArchiveManager.undoLast}
        />
        <Button
          icon="undo"
          text="Undo All"
          onClick={UndoArchiveManager.undoAll}
        />
        <Button
          text="Confirm Delete"
          intent="danger"
          onClick={UndoArchiveManager.clearQueue}
        />
      </ButtonGroup>
    </Card>
  );
};

export default SurveyDetailsNavigator;
